import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    Rating,
    CircularProgress,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import { useTranslate, useNotify } from 'react-admin';
import { ratingService, CourseRating } from '../../services/ratingService';

interface RatingDialogProps {
    open: boolean;
    courseId: number;
    courseName: string;
    existingRating?: CourseRating | null;
    onClose: () => void;
    onSubmit: () => void;
}

const labels: { [index: number]: string } = {
    1: 'courseRating.labels.terrible',
    2: 'courseRating.labels.poor',
    3: 'courseRating.labels.ok',
    4: 'courseRating.labels.good',
    5: 'courseRating.labels.excellent',
};

const RatingDialog: React.FC<RatingDialogProps> = ({
    open,
    courseId,
    courseName,
    existingRating,
    onClose,
    onSubmit,
}) => {
    const translate = useTranslate();
    const notify = useNotify();
    const [rating, setRating] = useState<number | null>(null);
    const [hover, setHover] = useState(-1);
    const [comment, setComment] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (existingRating) {
            setRating(existingRating.rating);
            setComment(existingRating.comment || '');
        } else {
            setRating(null);
            setComment('');
        }
    }, [existingRating, open]);

    const handleSave = async () => {
        if (rating === null) {
            notify(translate('courseRating.errors.ratingRequired'), { type: 'warning' });
            return;
        }

        setLoading(true);
        try {
            if (existingRating) {
                await ratingService.updateRating(existingRating.id, rating, comment);
                notify(translate('courseRating.updateSuccess'), { type: 'success' });
            } else {
                await ratingService.submitRating(courseId, rating, comment);
                notify(translate('courseRating.submitSuccess'), { type: 'success' });
            }
            onSubmit();
            onClose();
        } catch (error: any) {
            notify(error.message || translate('courseRating.errors.saveFailed'), { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!existingRating) return;

        setLoading(true);
        try {
            await ratingService.deleteRating(existingRating.id);
            notify(translate('courseRating.deleteSuccess'), { type: 'success' });
            onSubmit();
            onClose();
        } catch (error: any) {
            notify(error.message || translate('courseRating.errors.deleteFailed'), { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {existingRating
                    ? translate('courseRating.editTitle')
                    : translate('courseRating.title')}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
                    <Typography variant="body1" color="text.secondary">
                        {courseName}
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <Rating
                            name="course-rating"
                            value={rating}
                            size="large"
                            onChange={(event, newValue) => {
                                setRating(newValue);
                            }}
                            onChangeActive={(event, newHover) => {
                                setHover(newHover);
                            }}
                            emptyIcon={<StarIcon style={{ opacity: 0.55 }} fontSize="inherit" />}
                            sx={{
                                fontSize: '3rem',
                            }}
                        />
                        {rating !== null && (
                            <Typography variant="body2" color="text.secondary">
                                {translate(labels[hover !== -1 ? hover : rating])}
                            </Typography>
                        )}
                    </Box>

                    <TextField
                        label={translate('courseRating.comment')}
                        placeholder={translate('courseRating.commentPlaceholder')}
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        fullWidth
                        multiline
                        rows={4}
                        inputProps={{ maxLength: 1000 }}
                        helperText={`${comment.length}/1000`}
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                {existingRating && (
                    <Button
                        onClick={handleDelete}
                        color="error"
                        disabled={loading}
                        sx={{ mr: 'auto' }}
                    >
                        {translate('ra.action.delete')}
                    </Button>
                )}
                <Button onClick={onClose} disabled={loading}>
                    {translate('ra.action.cancel')}
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={loading || rating === null}
                    startIcon={loading ? <CircularProgress size={16} /> : null}
                >
                    {translate('ra.action.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default RatingDialog;
